import React from 'react';
import { motion } from 'framer-motion';
import { MolecularBackdrop } from '../components/MolecularBackdrop';
import { Gift, Sparkles } from 'lucide-react';
interface Props {
  onStart: () => void;
}
const PRIZES = [{
  img: 'https://cdn.magicpatterns.com/uploads/ujePgTpDb12uzDhbkb55p6/Screenshot_2026-05-12_at_13.38.08.png',
  name: 'Apple Watch',
  // top-left
  startX: '12%',
  startY: '14%',
  size: 120,
  duration: 14,
  delay: 0,
  driftX: 40,
  driftY: 30
}, {
  img: 'https://cdn.magicpatterns.com/uploads/jvyQwtycoWHav7BNcPZSoP/Screenshot_2026-05-12_at_13.39.05.png',
  name: 'AirPods Pro',
  // top-right
  startX: '68%',
  startY: '10%',
  size: 130,
  duration: 16,
  delay: 0.4,
  driftX: -35,
  driftY: 45
}, {
  img: 'https://cdn.magicpatterns.com/uploads/tZivAXHZnHGZ2WRrcqrFTv/Screenshot_2026-05-12_at_13.48.33.png',
  name: 'Bose Headphones',
  // mid-left
  startX: '6%',
  startY: '54%',
  size: 110,
  duration: 17,
  delay: 0.8,
  driftX: 50,
  driftY: -40
}, {
  img: 'https://cdn.magicpatterns.com/uploads/d7rszpu6Q1uohFoCZwoQJr/Screenshot_2026-05-12_at_13.49.04.png',
  name: 'Sonos Speaker',
  // mid-right
  startX: '70%',
  startY: '58%',
  size: 115,
  duration: 15,
  delay: 1.2,
  driftX: -45,
  driftY: -35
}, {
  img: 'https://cdn.magicpatterns.com/uploads/jtaH57imdgTkBTn1Vb8Ciy/Screenshot_2026-05-12_at_13.49.46.png',
  name: 'Kindle Scribe',
  // bottom-center
  startX: '36%',
  startY: '74%',
  size: 105,
  duration: 18,
  delay: 1.6,
  driftX: 30,
  driftY: -50
}];
export function SlideScreensaver({
  onStart
}: Props) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.8
  }} onClick={onStart} className="absolute inset-0 z-50 bg-gradient-to-br from-[#EAF2FA] via-[#F4F8FC] to-[#DCE7F4] flex flex-col items-center justify-center overflow-hidden cursor-pointer" data-id="element-566">
      <MolecularBackdrop variant="calm" data-id="element-567" />

      {/* Soft ambient blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] rounded-full bg-minerva-blue/10 blur-3xl pointer-events-none" data-id="element-568" />
      <div className="absolute bottom-[-15%] right-[-15%] w-[70%] h-[70%] rounded-full bg-cyan-200/20 blur-3xl pointer-events-none" data-id="element-569" />

      {/* Floating Prizes */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-570">
        {PRIZES.map((prize, i) => <motion.div key={i} className="absolute rounded-3xl bg-white/90 backdrop-blur-sm shadow-[0_20px_50px_-12px_rgba(0,80,114,0.25)] border border-white p-3 flex items-center justify-center" style={{
        left: prize.startX,
        top: prize.startY,
        width: prize.size,
        height: prize.size
      }} initial={{
        opacity: 0,
        scale: 0.4,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        x: [0, prize.driftX, -prize.driftX * 0.5, 0],
        y: [30, -prize.driftY, prize.driftY * 0.5, 30],
        rotate: [0, 4, -4, 0]
      }} transition={{
        opacity: {
          duration: 1,
          delay: prize.delay
        },
        scale: {
          duration: 1,
          delay: prize.delay,
          type: 'spring',
          damping: 12
        },
        x: {
          duration: prize.duration,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: prize.delay
        },
        y: {
          duration: prize.duration + 2,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: prize.delay
        },
        rotate: {
          duration: prize.duration + 4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: prize.delay
        }
      }} data-id="element-571">
            <img src={prize.img} alt={prize.name} className="w-full h-full object-contain mix-blend-multiply" draggable={false} data-id="element-572" />

            {/* subtle glow ring */}
            <div className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/60 pointer-events-none" data-id="element-573" />
          </motion.div>)}
      </div>

      {/* Center Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6" data-id="element-574">
        <motion.div initial={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="bg-white/85 backdrop-blur-xl px-7 py-7 rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,133,189,0.3)] border border-white/60 max-w-[300px]" data-id="element-575">
          <div className="flex items-center justify-center gap-1.5 mb-3" data-id="element-576">
            <Sparkles size={12} className="text-minerva-blue" data-id="element-577" />
            <span className="text-[10px] font-bold tracking-[0.25em] text-minerva-blue uppercase" data-id="element-578">
              SNMMI 2026 · Daily Draw
            </span>
            <Sparkles size={12} className="text-minerva-blue" data-id="element-579" />
          </div>

          <div className="w-12 h-12 mx-auto bg-gradient-to-br from-minerva-blue to-cyan-500 rounded-full flex items-center justify-center mb-3 shadow-lg shadow-minerva-blue/30" data-id="element-580">
            <Gift size={22} className="text-white" data-id="element-581" />
          </div>

          <h1 className="text-[26px] font-display font-bold text-slate-900 leading-[1.1] mb-2" data-id="element-582">
            Win Premium Prizes
          </h1>
          <p className="text-slate-600 font-medium text-[13px] leading-snug mb-5" data-id="element-583">
            Take the 60-second Industry Pulse to enter today's draw.
          </p>

          <motion.div animate={{
          opacity: [0.5, 1, 0.5],
          scale: [1, 1.03, 1]
        }} transition={{
          duration: 2.2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-slate-900 text-white text-[12px] font-bold tracking-[0.18em] uppercase shadow-lg" data-id="element-584">
            Tap anywhere to begin
          </motion.div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 0.6
      }} transition={{
        delay: 1.2,
        duration: 1
      }} className="mt-5 text-[10px] font-semibold tracking-[0.3em] uppercase text-slate-500" data-id="element-585">
          Visit Booth #1762 · Double your chances
        </motion.p>
      </div>
    </motion.div>;
}